/**
 * HTTP layer for the `mockData.json` sidecar. Mirrors {@link nodesMetadataApi}:
 *   - Local mode: Vite plugin endpoint `/local/workflows/:name/mockData`
 *   - Cloud mode: VFS PUT/GET next to the workflow at
 *     `${vfsBase}/${workflowName}/mockData.json`
 *
 * The sidecar is a single flat (draft-only) file — see `services/mockData.ts`
 * for the shape. Callers should fetch on workflow load and PUT alongside
 * workflow saves.
 */

import {
  isLocalMode,
  getLocalPluginUrl,
  localFetch,
} from './localRuntimeClient';
import { AppContext, appFetch, getNewVfsUrl } from './appClient';
import { apiVersions } from './endpoints';
import { telemetryService } from '@/services/telemetryService';
import {
  type MockDataFile,
  isEmptyMockData,
  parseMockData,
} from './mockData';

export type MockDataOptions = AppContext & {
  workflowName: string;
};

const SIDECAR_FILE = 'mockData.json';

function cloudUrl(opts: MockDataOptions): string {
  const vfsBase = getNewVfsUrl(opts);
  return `${vfsBase}/${opts.workflowName}/${SIDECAR_FILE}?api-version=${apiVersions.otto}`;
}

function localUrl(opts: MockDataOptions): string {
  return `${getLocalPluginUrl()}/local/workflows/${opts.workflowName}/mockData`;
}

/**
 * Fetch the sidecar. Returns `null` when the file does not exist (first save
 * for this workflow), is unparseable, or was written but never populated.
 */
export async function getMockData(
  opts: MockDataOptions,
): Promise<MockDataFile | null> {
  if (isLocalMode()) {
    try {
      // Plugin returns 404 (with a JSON error body) when the sidecar file is
      // absent; localFetch throws on non-2xx, so the common "no sidecar yet"
      // case falls into the catch below and yields null.
      const raw = await localFetch<unknown>(localUrl(opts));
      if (raw == null) return null;
      const parsed = parseMockData(raw);
      return isEmptyMockData(parsed) ? null : parsed;
    } catch {
      return null;
    }
  }

  const accessToken = await opts.getToken();
  if (!accessToken) return null;

  try {
    const res = await appFetch<unknown>(cloudUrl(opts), accessToken, { method: 'GET' });
    if (res == null) return null;
    const parsed = parseMockData(res);
    return isEmptyMockData(parsed) ? null : parsed;
  } catch {
    // 404 (no sidecar yet) is the common case — silently fall through.
    return null;
  }
}

/**
 * Write the sidecar. Returns `true` on success. Failures are logged via
 * telemetry but don't throw — the workflow save shouldn't fail because the
 * sidecar PUT couldn't complete (the workflow itself is the contract).
 */
export async function saveMockData(
  opts: MockDataOptions,
  file: MockDataFile,
): Promise<boolean> {
  const start = performance.now();

  if (isLocalMode()) {
    try {
      await localFetch(localUrl(opts), { method: 'POST', body: file });
      telemetryService.trackMetric('api.response_time', performance.now() - start, {
        endpoint: 'saveMockData',
        method: 'POST',
        statusCode: '200',
      });
      return true;
    } catch (err) {
      telemetryService.trackEvent('api.error', {
        endpoint: 'saveMockData',
        method: 'POST',
        statusCode: 'unknown',
        error: (err instanceof Error ? err.message : String(err)).slice(0, 200),
      });
      return false;
    }
  }

  const accessToken = await opts.getToken();
  if (!accessToken) return false;

  try {
    await appFetch(cloudUrl(opts), accessToken, {
      method: 'PUT',
      headers: { 'If-Match': '*' },
      body: JSON.stringify(file),
    });
    telemetryService.trackMetric('api.response_time', performance.now() - start, {
      endpoint: 'saveMockData',
      method: 'PUT',
      statusCode: '200',
    });
    return true;
  } catch (err) {
    telemetryService.trackEvent('api.error', {
      endpoint: 'saveMockData',
      method: 'PUT',
      statusCode: 'unknown',
      error: (err instanceof Error ? err.message : String(err)).slice(0, 200),
    });
    return false;
  }
}
